import { type ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import '@tokens/colors.stylex';
import { colors } from '@tokens/colors.stylex';
import { lightTheme } from './themes/light';
import { darkTheme } from './themes/dark';
export type ResolvedTheme = 'light' | 'dark';
interface ThemeProviderProps {
  theme: ResolvedTheme;
  children: ComponentChildren;
}
const themeMap = {
  light: lightTheme,
  dark: darkTheme
} satisfies Record<ResolvedTheme, typeof lightTheme>;
const styles = {
  root: {
    kAzted: "xg6iff7",
    kzqmXN: "xh8yej3",
    kWkggS: "x16vpd03",
    kMwMTN: "x1oezkus",
    $$css: true
  }
};
export function ThemeProvider({
  theme,
  children
}: ThemeProviderProps) {
  // `data-theme-root` is what portals aim at. The theme is a CLASS on this div,
  // not on <html>, so anything portaled to `document.body` lands OUTSIDE it and
  // renders untokenised — white surface, black ink, in dark mode (the row-action
  // popover shipped that way, 2026-09-16). See `portalTarget`.
  return <div data-theme-root="" {...stylex.props(styles.root, themeMap[theme])}>
      {children}
    </div>;
}

/**
 * Where a floating layer should portal to: the themed root, falling back to
 * `document.body`.
 *
 * ⚠ NOT `document.body` directly. Escaping an ancestor's `overflow` is why these
 * portal at all — but body is also outside the ThemeProvider, so the escape cost
 * them every theme token. The theme root is the outermost app element and is not
 * itself a clipping context, so it satisfies both.
 */
export function portalTarget(): HTMLElement {
  if (typeof document === 'undefined') return null as unknown as HTMLElement;
  return document.querySelector<HTMLElement>('[data-theme-root]') ?? document.body;
}

/** Syncs the resolved theme to <html> data-theme attr for any non-StyleX consumers (scrollbars etc.) */
export function useHtmlThemeSync(theme: ResolvedTheme) {
  const prev = useRef<ResolvedTheme | null>(null);
  useEffect(() => {
    if (prev.current === theme) return;
    document.documentElement.setAttribute('data-theme', theme);
    prev.current = theme;
  }, [theme]);
}